<template>
    <div>
      <breadcrumb :items="breadcrumbs" />
      <v-container fluid>
  
        <div class="d-flex align-center mb-6">
          <v-btn icon class="mr-2" :to="{ name: 'iybScoreIndex' }">
            <v-icon>mdi-arrow-left</v-icon>
          </v-btn>
          <h2>{{ $t('dueDiligence.iyb.importSpreadsheetTitle') }}</h2>
        </div>
  
        <!-- Step indicator -->
        <v-stepper v-model="currentStep" elevation="0" class="mb-6" style="background:transparent;">
          <v-stepper-header style="box-shadow:none; border:1px solid #e0e0e0; border-radius:8px;">
            <v-stepper-step :complete="currentStep > 1" step="1" color="primary">{{ $t('dueDiligence.iyb.uploadFile') }}</v-stepper-step>
            <v-divider />
            <v-stepper-step :complete="currentStep > 2" step="2" color="primary">{{ $t('dueDiligence.iyb.previewData') }}</v-stepper-step>
            <v-divider />
            <v-stepper-step step="3" color="primary">{{ $t('dueDiligence.iyb.confirmImport') }}</v-stepper-step>
          </v-stepper-header>
        </v-stepper>
  
        <!-- Step 1: Upload -->
        <v-card elevation="0" v-if="currentStep === 1">
          <v-card-text class="px-6 py-6">
            <div class="text-h6 font-weight-bold mb-1">{{ $t('dueDiligence.iyb.uploadPtsiSpreadsheet') }}</div>
            <div class="text-body-2 text--secondary mb-6">
              {{ $t('dueDiligence.iyb.uploadDescription') }}
              {{ $t('dueDiligence.iyb.acceptedFormats') }}: <strong>.csv</strong> {{ $t('dueDiligence.iyb.or') }} <strong>.xlsx</strong>.
            </div>
  
            <!-- File Drop Zone -->
            <div
              class="upload-zone pa-10 text-center mb-4"
              :class="{ 'upload-zone--active': isDragging }"
              @dragover.prevent="isDragging = true"
              @dragleave="isDragging = false"
              @drop.prevent="handleDrop"
              @click="$refs.fileInput.click()"
              style="border:2px dashed #00BD73; border-radius:12px; cursor:pointer; background:#f8fffe;"
            >
              <v-icon size="52" color="primary" class="mb-3">mdi-file-table</v-icon>
              <div class="text-body-1 font-weight-medium mb-1">
                {{ selectedFile ? selectedFile.name : $t('dueDiligence.iyb.clickOrDragFileHere') }}
              </div>
              <div class="text-caption text--secondary">
                {{ selectedFile ? `${(selectedFile.size / 1024).toFixed(0)} KB` : $t('dueDiligence.iyb.acceptsCsvOrXlsx') }}
              </div>
              <input ref="fileInput" type="file" accept=".csv,.xlsx" style="display:none;" @change="handleFileSelect" />
            </div>
  
            <!-- Data Year -->
            <v-row>
              <v-col cols="12" sm="6">
                <v-select
                  v-model="dataYear"
                  :items="yearOptions"
                  :label="$t('dueDiligence.iyb.dataYear')"
                  outlined
                  dense
                  prepend-inner-icon="mdi-calendar"
                />
                <div class="text-caption text--secondary mt-0 pt-0">
                  <v-icon x-small class="mr-1">mdi-information-outline</v-icon>
                  {{ $t('dueDiligence.iyb.selectYearHint') }}
                </div>
              </v-col>
            </v-row>
  
            <div class="d-flex justify-end mt-4">
              <v-btn
                color="primary"
                dark
                :disabled="!selectedFile || !dataYear"
                @click="previewFile"
                :loading="previewing"
              >
                <v-icon :style="{ color: '#ffffff !important' }" small class="mr-1">mdi-eye</v-icon>
                {{ $t('dueDiligence.iyb.previewData') }}
              </v-btn>
            </div>
          </v-card-text>
        </v-card>
  
        <!-- Step 2: Preview -->
        <v-card elevation="0" v-if="currentStep === 2">
          <v-card-text class="px-6 py-6">
            <div class="d-flex align-center mb-4">
              <div>
                <div class="text-h6 font-weight-bold mb-1">{{ $t('dueDiligence.iyb.previewImportData') }}</div>
                <div class="text-body-2 text--secondary">
                  {{ $t('dueDiligence.iyb.showingRows', { from: previewFromCount, to: previewToCount }) }}.
                  {{ $t('dueDiligence.iyb.totalRegenciesFound') }}: <strong>{{ previewData.total }}</strong>.
                </div>
              </div>
              <v-spacer />
              <v-chip small class="manually-button">
                <v-icon x-small :style="{ color: '#ffffff !important' }" class="mr-1">mdi-check</v-icon>
                {{ previewData.total }} {{ $t('dueDiligence.iyb.records') }}
              </v-chip>
              <div class="py-1 title ml-4">
                {{ previewFromCount }} - {{ previewToCount }} of {{ previewData.total }}
              </div>
              <v-btn
                class="mx-1"
                fab
                small
                outlined
                color="primary"
                :disabled="previewPage <= 1 || previewing"
                @click="changePreviewPage(false)"
              >
                <v-icon>mdi-chevron-left</v-icon>
              </v-btn>
              <v-btn
                class="mx-1"
                fab
                small
                outlined
                color="primary"
                :disabled="previewPage >= previewTotalPages || previewing"
                @click="changePreviewPage(true)"
              >
                <v-icon>mdi-chevron-right</v-icon>
              </v-btn>
            </div>
  
            <v-data-table
              :headers="previewHeaders"
              :items="previewData.rows"
              :items-per-page="10"
              hide-default-footer
              :loading="previewing"
              elevation="0"
              class="exchange-table"
            >
              <template v-slot:item.compositeScore="{ item }">
                <span
                  class="font-weight-bold"
                  :style="'color:' + getScoreColor(item.compositeScore)"
                >
                  {{ item.compositeScore != null ? item.compositeScore.toFixed(2) : '—' }}
                </span>
              </template>
              <template v-slot:item.firePrevention="{ item }">
                <template v-if="item.firePrevention != null">
                  <v-tooltip bottom max-width="260">
                    <template v-slot:activator="{ on }">
                      <v-chip
                        v-on="on"
                        x-small
                        :class="classifyFirePrev(item.firePrevention).badgeClass"
                        style="min-width:76px; justify-content:center; font-size:10px;"
                      >
                        {{ item.firePrevention.toFixed(1) }}
                      </v-chip>
                    </template>
                    <span>{{ $t('dueDiligence.iyb.firePreventionTooltip') }}</span>
                  </v-tooltip>
                </template>
                <span v-else class="text--secondary text-caption">—</span>
              </template>
              <template v-slot:item.eudrRiskStatus="{ item }">
                <v-btn x-small rounded depressed class="iyb-risk-btn" :class="getRiskClass(deriveRiskStatus(item))" min-width="120" height="26">
                  {{ normalizeRiskStatusLabel(deriveRiskStatus(item)) || 'N/A' }}
                </v-btn>
              </template>
            </v-data-table>

            <!-- Summary stats -->
            <v-row class="mt-4">
              <v-col cols="4">
                <div class="pa-3 text-center" style="border:1px solid #C1FFE7; border-radius:8px; background:#f8fff8;">
                  <div class="text-h5 font-weight-bold" style="color:#489E16;">{{ previewData.greenCount }}</div>
                  <div class="text-caption">{{ $t('dueDiligence.iyb.lowRiskFastTrack') }}</div>
                </div>
              </v-col>
              <v-col cols="4">
                <div class="pa-3 text-center" style="border:1px solid #FFA826; border-radius:8px; background:#fffdf8;">
                  <div class="text-h5 font-weight-bold" style="color:#FFA826;">{{ previewData.yellowCount }}</div>
                  <div class="text-caption">{{ $t('dueDiligence.iyb.mediumRiskMitigation') }}</div>
                </div>
              </v-col>
              <v-col cols="4">
                <div class="pa-3 text-center" style="border:1px solid #DA3D3D; border-radius:8px; background:#fffafa;">
                  <div class="text-h5 font-weight-bold" style="color:#DA3D3D;">{{ previewData.redCount }}</div>
                  <div class="text-caption">{{ $t('dueDiligence.iyb.highRiskAppealPath') }}</div>
                </div>
              </v-col>
            </v-row>
  
            <div class="d-flex justify-space-between mt-6">
              <v-btn text @click="currentStep = 1">
                <v-icon small class="mr-1">mdi-arrow-left</v-icon>
                {{ $t('deforestation.back') }}
              </v-btn>
              <v-btn color="primary" dark @click="currentStep = 3">
                {{ $t('dueDiligence.iyb.proceedToConfirm') }}
                <v-icon small class="ml-1">mdi-arrow-right</v-icon>
              </v-btn>
            </div>
          </v-card-text>
        </v-card>
  
        <!-- Step 3: Confirm Import -->
        <v-card elevation="0" v-if="currentStep === 3">
          <v-card-text class="px-6 py-6">
            <div class="text-h6 font-weight-bold mb-1">{{ $t('dueDiligence.iyb.confirmImport') }}</div>
            <div class="text-body-2 text--secondary mb-6">
              {{ $t('dueDiligence.iyb.confirmImportDescription', { total: previewData.total, year: dataYear }) }}
            </div>
  
            <div class="pa-4 mb-4" style="background:#f5f5f5; border-radius:8px;">
              <v-row dense>
                <v-col cols="6">
                  <div class="text-caption text--secondary">{{ $t('deforestation.fileName') }}</div>
                  <div class="text-body-2 font-weight-medium">{{ selectedFile ? selectedFile.name : '' }}</div>
                </v-col>
                <v-col cols="3">
                  <div class="text-caption text--secondary">{{ $t('dueDiligence.iyb.dataYear') }}</div>
                  <div class="text-body-2 font-weight-medium">{{ dataYear }}</div>
                </v-col>
                <v-col cols="3">
                  <div class="text-caption text--secondary">{{ $t('dueDiligence.iyb.totalRegencies') }}</div>
                  <div class="text-body-2 font-weight-medium">{{ previewData.total }}</div>
                </v-col>
              </v-row>
            </div>
  
            <!-- Import Progress -->
            <div v-if="importing" class="mb-4">
              <div class="d-flex justify-space-between mb-1">
                <span class="text-caption">{{ $t('deforestation.uploading') }}...</span>
                <span class="text-caption">{{ importProgress }}%</span>
              </div>
              <v-progress-linear
                :value="importProgress"
                color="primary"
                height="6"
                rounded
              />
            </div>
  
            <div class="d-flex justify-space-between mt-4">
              <v-btn text :disabled="importing" @click="currentStep = 2">
                <v-icon small class="mr-1">mdi-arrow-left</v-icon>
                {{ $t('deforestation.back') }}
              </v-btn>
              <v-btn
                color="primary"
                dark
                :loading="importing"
                @click="confirmImport"
              >
                <v-icon small :style="{ color: '#ffffff !important' }" class="mr-1">mdi-database-import</v-icon>
                {{ $t('dueDiligence.iyb.importNow') }}
              </v-btn>
            </div>
          </v-card-text>
        </v-card>
  
      </v-container>
    </div>
  </template>
  
  <script>
  import {
    getIybRiskScoreColor,
    normalizeRiskStatusLabel,
    classifyIybIndicatorValue,
    deriveIybRiskStatus,
    getIybRiskBadgeClass,
  } from '@/utils/iybRiskClassification'
  import IYBScoreService from '@/_services/IYBScoreService'
  export default {
    name: 'ImportIYBSpreadsheet',
    data() {
      const currentYear = new Date().getFullYear()
      return {
        currentStep: 1,
        isDragging: false,
        selectedFile: null,
        dataYear: currentYear,
        previewing: false,
        importing: false,
        importProgress: 0,
        previewData: {
          total: 0,
          page: 1,
          limit: 10,
          count: 0,
          totalPages: 1,
          rows: [],
          greenCount: 0,
          yellowCount: 0,
          redCount: 0,
        },
        previewPage: 1,
        previewLimit: 10,
        yearOptions: Array.from({ length: 5 }, (_, index) => currentYear - 4 + index),
        previewHeaders: [
          { text: this.$t('dueDiligence.iyb.regencyId'), value: 'kabProfileId', sortable: false, width: '120px' },
          { text: this.$t('dueDiligence.iyb.regencyName'), value: 'regencyName', sortable: false, width: '200px' },
          { text: this.$t('dueDiligence.iyb.iybScore'), value: 'compositeScore', sortable: false, align: 'center', width: '110px' },
          { text: this.$t('dueDiligence.iyb.firePrevention'), value: 'firePrevention', sortable: false, align: 'center', width: '140px' },
          { text: this.$t('dueDiligence.iyb.eudrRiskStatus'), value: 'eudrRiskStatus', sortable: false, align: 'center', width: '160px' },
        ],
        breadcrumbs: [
          { text: this.$t('deforestation.dueDeligence'), disabled: false, exact: true },
          { text: this.$t('dueDiligence.iyb.iybScores'), disabled: false, exact: true, to: { name: 'iybScoreIndex' } },
          { text: this.$t('dueDiligence.iyb.importSpreadsheet'), disabled: true },
        ],
      }
    },
    computed: {
      previewTotalPages() {
        return this.previewData.totalPages || 1
      },
      previewFromCount() {
        if (!this.previewData.total) return 0
        return (this.previewPage - 1) * this.previewLimit + 1
      },
      previewToCount() {
        if (!this.previewData.total) return 0
        return Math.min(this.previewPage * this.previewLimit, this.previewData.total)
      },
    },
    methods: {
      handleFileSelect(e) {
        const file = e.target.files[0]
        if (file) this.selectedFile = file
      },
      handleDrop(e) {
        this.isDragging = false
        const file = e.dataTransfer.files[0]
        if (file && (file.name.endsWith('.xlsx') || file.name.endsWith('.csv'))) this.selectedFile = file
      },
      async previewFile() {
        if (!this.selectedFile || !this.dataYear) return
        this.previewing = true
        this.previewPage = 1
        this.previewData = {
          total: 0,
          page: 1,
          limit: this.previewLimit,
          count: 0,
          totalPages: 1,
          rows: [],
          greenCount: 0,
          yellowCount: 0,
          redCount: 0,
        }
        try {
          const data = await IYBScoreService.previewFile(this.selectedFile, this.dataYear, this.previewPage, this.previewLimit)
          this.setPreviewData(data)
          if (!this.previewData.total) {
            this.$notify({
              title: this.$t('dueDiligence.iyb.noDataFound'),
              text: this.$t('dueDiligence.iyb.noValidRegencyRows'),
              type: 'warning',
            })
            return
          }
          this.currentStep = 2
        } catch (e) {
          console.error(e)
          this.$notify({
            title: this.$t('dueDiligence.iyb.previewFailed'),
            text: e.response?.data?.message || e.message || this.$t('dueDiligence.iyb.couldNotParseSpreadsheet'),
            type: 'error',
          })
        } finally {
          this.previewing = false
        }
      },
      async changePreviewPage(next) {
        const target = next ? this.previewPage + 1 : this.previewPage - 1
        if (target < 1 || target > this.previewTotalPages) return
        this.previewing = true
        try {
          const data = await IYBScoreService.previewFile(this.selectedFile, this.dataYear, target, this.previewLimit)
          this.previewPage = target
          this.setPreviewData(data)
        } catch (e) {
          console.error(e)
          this.$notify({
            title: this.$t('dueDiligence.iyb.previewFailed'),
            text: e.response?.data?.message || e.message || this.$t('dueDiligence.iyb.couldNotLoadPreviewPage'),
            type: 'error',
          })
        } finally {
          this.previewing = false
        }
      },
      setPreviewData(data) {
        this.previewData = {
          total: data.total ?? 0,
          page: data.page ?? this.previewPage,
          limit: data.limit ?? this.previewLimit,
          count: data.count ?? (data.rows || []).length,
          totalPages: data.totalPages ?? 1,
          rows: data.rows ?? [],
          greenCount: data.greenCount ?? 0,
          yellowCount: data.yellowCount ?? 0,
          redCount: data.redCount ?? 0,
        }
      },
      async confirmImport() {
        this.importing = true
        this.importProgress = 0
        let processingTimer = null
        try {
          processingTimer = setInterval(() => {
            if (this.importProgress < 90) {
              this.importProgress += 3
            }
          }, 250)

          const result = await IYBScoreService.importFile(
            this.selectedFile,
            this.dataYear,
            (pct) => {
              this.importProgress = Math.max(this.importProgress, pct)
            },
          )
          this.importProgress = 100
          if (processingTimer) clearInterval(processingTimer)
          this.$notify({
            title: this.$t('dueDiligence.iyb.importSuccessTitle'),
            text: this.$t('dueDiligence.iyb.regenciesImported', { count: result.imported || 0 }),
            type: 'success',
          })
          this.$router.push({ name: 'iybScoreIndex' })
        } catch (e) {
          if (processingTimer) clearInterval(processingTimer)
          console.error(e)
          this.$notify({
            title: this.$t('dueDiligence.iyb.importFailed'),
            text: e.response?.data?.message || e.message || this.$t('dueDiligence.iyb.couldNotImportSpreadsheet'),
            type: 'error',
          })
        } finally {
          if (processingTimer) clearInterval(processingTimer)
          this.importing = false
        }
      },
      normalizeRiskStatusLabel,
      deriveRiskStatus(item) {
        return deriveIybRiskStatus(item)
      },
      getRiskClass(status) {
        return getIybRiskBadgeClass(status)
      },
      getScoreColor(score) {
        return getIybRiskScoreColor(score)
      },
      classifyFirePrev(val) {
        return classifyIybIndicatorValue(val)
      },
    }
  }
  </script>

  <style scoped>
  .iyb-risk-btn {
    text-transform: none;
    letter-spacing: normal;
    font-weight: 600;
  }

  .iyb-badge--green {
    background-color: #e8f5e9 !important;
    border: 2px solid #2e7d32 !important;
    color: #1b5e20 !important;
  }

  .iyb-badge--yellow {
    background-color: #fff8e1 !important;
    border: 2px solid #FFA826 !important;
    color: #E65100 !important;
  }

  .iyb-badge--red {
    background-color: #ffebee !important;
    border: 2px solid #c62828 !important;
    color: #b71c1c !important;
  }

  .iyb-risk-btn.iyb-badge--green >>> .v-btn__content,
  .iyb-risk-btn.iyb-badge--yellow >>> .v-btn__content,
  .iyb-risk-btn.iyb-badge--red >>> .v-btn__content {
    color: inherit !important;
  }
  </style>